// deno-lint-ignore-file no-unused-vars
import { h, Fragment } from '../jsx-runtime.ts';
import type { TaskWithTags, ListFilters } from '../types.ts';
import { TasksAPI } from '../api.ts';
import { showToast } from './tmk-toast.tsx';

const DAY_NAMES = ['Pon', 'Wt', 'Śr', 'Czw', 'Pt', 'Sob', 'Ndz'];
const MONTH_NAMES = [
  'Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec',
  'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień',
];

class TmkCalendar extends HTMLElement {
  private currentDate = new Date();
  private viewMode: 'month' | 'week' = window.innerWidth < 768 ? 'week' : 'month';
  private filters: ListFilters = {};

  async render(opts: { filters?: ListFilters } = {}) {
    this.filters = opts.filters ?? {};
    this.innerHTML = '';

    const { start, end } = this.getDateRange();

    let tasks: TaskWithTags[] = [];
    try {
      tasks = await TasksAPI.list({
        ...this.filters,
        date_from: formatDate(start),
        date_to: formatDate(end),
      });
    } catch (err: unknown) {
      showToast('Nie udało się załadować zadań: ' + (err as Error).message, 'error');
    }

    const allTasks = flattenTasks(tasks);

    const wrapper = <div className="calendar-wrapper" /> as HTMLElement;
    wrapper.appendChild(this.buildHeader());
    wrapper.appendChild(this.buildDayNamesRow());
    wrapper.appendChild(this.buildGrid(allTasks, start, end));

    this.appendChild(wrapper);
  }

  // ─── Header with navigation ───────────────────────────────────────────

  private buildHeader(): HTMLElement {
    const header = <div className="calendar-header" /> as HTMLElement;

    // Prev
    const prevBtn = <button className="calendar-nav-btn">{'◀'}</button> as HTMLElement;
    prevBtn.addEventListener('click', () => this.navigate(-1));
    header.appendChild(prevBtn);

    // Title
    const titleText = this.viewMode === 'month'
      ? MONTH_NAMES[this.currentDate.getMonth()] + ' ' + this.currentDate.getFullYear()
      : (() => { const { start, end } = this.getDateRange(); return formatDate(start) + ' — ' + formatDate(end); })();
    header.appendChild(<span className="calendar-title">{titleText}</span> as HTMLElement);

    // Next
    const nextBtn = <button className="calendar-nav-btn">{'▶'}</button> as HTMLElement;
    nextBtn.addEventListener('click', () => this.navigate(1));
    header.appendChild(nextBtn);

    // Today
    const todayBtn = <button className="calendar-nav-btn calendar-today-btn">Dziś</button> as HTMLElement;
    todayBtn.addEventListener('click', () => {
      this.currentDate = new Date();
      this.render({ filters: this.filters });
    });
    header.appendChild(todayBtn);

    // View toggle
    const toggleBtn = <button className="calendar-nav-btn calendar-toggle-btn">
      {this.viewMode === 'month' ? 'Tydzień' : 'Miesiąc'}
    </button> as HTMLElement;
    toggleBtn.addEventListener('click', () => {
      this.viewMode = this.viewMode === 'month' ? 'week' : 'month';
      this.render({ filters: this.filters });
    });
    header.appendChild(toggleBtn);

    return header;
  }

  // ─── Day names row ────────────────────────────────────────────────────

  private buildDayNamesRow(): HTMLElement {
    const row = <div className="calendar-grid calendar-day-names" /> as HTMLElement;
    for (const name of DAY_NAMES) {
      row.appendChild(<div className="calendar-day-name">{name}</div> as HTMLElement);
    }
    return row;
  }

  // ─── Grid ─────────────────────────────────────────────────────────────

  private buildGrid(tasks: TaskWithTags[], rangeStart: Date, rangeEnd: Date): HTMLElement {
    const grid = <div className="calendar-grid" /> as HTMLElement;

    // Group tasks by deadline
    const tasksByDate = new Map<string, TaskWithTags[]>();
    for (const task of tasks) {
      if (!task.deadline) continue;
      const key = task.deadline;
      if (!tasksByDate.has(key)) tasksByDate.set(key, []);
      tasksByDate.get(key)!.push(task);
    }

    const today = formatDate(new Date());
    const currentMonth = this.currentDate.getMonth();

    // Grid start: back up to Monday
    const gridStart = new Date(rangeStart);
    const gridEnd = new Date(rangeEnd);

    if (this.viewMode === 'month') {
      const dayOfWeek = (gridStart.getDay() + 6) % 7;
      gridStart.setDate(gridStart.getDate() - dayOfWeek);
      const endDayOfWeek = (gridEnd.getDay() + 6) % 7;
      if (endDayOfWeek < 6) gridEnd.setDate(gridEnd.getDate() + (6 - endDayOfWeek));
    }

    const cursor = new Date(gridStart);
    while (cursor <= gridEnd) {
      const dateStr = formatDate(cursor);
      const isToday = dateStr === today;
      const isOtherMonth = this.viewMode === 'month' && cursor.getMonth() !== currentMonth;

      const classes = 'calendar-day' + (isToday ? ' today' : '') + (isOtherMonth ? ' other-month' : '');
      const cell = <div className={classes} data-date={dateStr} /> as HTMLElement;

      // Day number
      const dayNum = <div className="calendar-day-number">{String(cursor.getDate())}</div> as HTMLElement;
      cell.appendChild(dayNum);

      // Task cards
      const dayTasks = tasksByDate.get(dateStr) || [];
      for (const task of dayTasks) {
        const card = <div className="calendar-event">{task.title}</div> as HTMLElement;

        if (task.tags?.length && task.tags[0].tag_color) {
          card.style.borderLeftColor = task.tags[0].tag_color;
          card.style.background = task.tags[0].tag_color + '18';
        }

        card.addEventListener('click', (e: Event) => {
          e.stopPropagation();
          cell.dispatchEvent(new CustomEvent('calendar-task-click', {
            bubbles: true,
            detail: { taskId: task.id },
          }));
        });

        cell.appendChild(card);
      }

      // Click empty area to create task
      cell.addEventListener('click', (e: Event) => {
        if (e.target !== cell && e.target !== dayNum) return;
        this.createTaskOnDate(dateStr);
      });

      grid.appendChild(cell);
      cursor.setDate(cursor.getDate() + 1);
    }

    return grid;
  }

  // ─── Navigation ───────────────────────────────────────────────────────

  private navigate(direction: number) {
    if (this.viewMode === 'month') {
      this.currentDate.setMonth(this.currentDate.getMonth() + direction);
    } else {
      this.currentDate.setDate(this.currentDate.getDate() + (7 * direction));
    }
    this.render({ filters: this.filters });
  }

  // ─── Create task on date ──────────────────────────────────────────────

  private async createTaskOnDate(dateStr: string) {
    try {
      await TasksAPI.create({ title: '', deadline: dateStr });
      await this.render({ filters: this.filters });
    } catch (err: unknown) {
      showToast('Błąd tworzenia zadania: ' + (err as Error).message, 'error');
    }
  }

  // ─── Helpers ──────────────────────────────────────────────────────────

  private getDateRange(): { start: Date; end: Date } {
    if (this.viewMode === 'month') {
      const start = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth(), 1);
      const end = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() + 1, 0);
      return { start, end };
    } else {
      const d = new Date(this.currentDate);
      const day = (d.getDay() + 6) % 7;
      const start = new Date(d);
      start.setDate(d.getDate() - day);
      const end = new Date(start);
      end.setDate(start.getDate() + 6);
      return { start, end };
    }
  }
}

function flattenTasks(tasks: TaskWithTags[]): TaskWithTags[] {
  const result: TaskWithTags[] = [];
  for (const task of tasks) {
    result.push(task);
    if (task.subtasks?.length) {
      result.push(...flattenTasks(task.subtasks as TaskWithTags[]));
    }
  }
  return result;
}

function formatDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return y + '-' + m + '-' + d;
}

customElements.define('tmk-calendar', TmkCalendar);

export { TmkCalendar };
